import React from 'react';
import Chip from '@material-ui/core/Chip';
import CheckCircle from '@material-ui/icons/CheckCircle';
import HourglassEmpty from '@material-ui/icons/HourglassEmpty';
import TextInput from '../TextInput';
import DefaultButton from '../Buttons/DefaultButton/button';
import {
   MobileNumberDetails,
   MobileNumberValidationDetails,
   getFieldClassName,
   getViewWarningMessages,
} from './types';
import MobileNumberWarning, {
   MobileNumberRequirement,
} from './MobileNumberWarning';

export const LoadingStatusChip = () => (
   <Chip
      className="verification-status-chip verification-status-chip-loading"
      icon={<HourglassEmpty className="verification-status-chip-icon" />}
      label="Loading"
   />
);

// Only the verified state still has a chip. The unverified state is explained by the
// requirement label and warning below the input instead.
export const VerificationStatusChip = () => (
   <Chip
      className="verification-status-chip verification-status-chip-verified"
      icon={<CheckCircle className="verification-status-chip-icon" />}
      label="Verified"
   />
);

interface MobileNumberViewProps {
   mobileNumberDetails: MobileNumberDetails;
   validationDetails?: MobileNumberValidationDetails;
   isFetchingCurrentVerification?: boolean;
   isFetchingContactMobileNumber?: boolean;
   successMessage?: string | null;
   inputValid?: boolean;
   onEdit: () => void;
}

const MobileNumberView = ({
   mobileNumberDetails,
   validationDetails,
   isFetchingCurrentVerification = false,
   isFetchingContactMobileNumber = false,
   successMessage = null,
   inputValid,
   onEdit,
}: MobileNumberViewProps) => {
   const { mobileNumber, mobileNumberVerifiedDate } = mobileNumberDetails;
   const isLoading =
      isFetchingCurrentVerification || isFetchingContactMobileNumber;

   // Suppressed while a just-completed verification is being announced: the contact is
   // re-fetched at that point, so the verified date can still be missing for a moment and
   // a warning sitting beside the success message would contradict it.
   const needsAttention =
      !isLoading && !successMessage && !mobileNumberVerifiedDate;

   const renderStatusChip = () => {
      if (isLoading) return <LoadingStatusChip />;
      if (mobileNumberVerifiedDate) return <VerificationStatusChip />;
      return null;
   };

   return (
      <div className={getFieldClassName(inputValid)}>
         <div className="mobile-number-text-field-header">
            <span className="grey2-text input-header">
               Contact Mobile Number <span className="text-red">*</span>
            </span>
            {renderStatusChip()}
         </div>
         <TextInput
            type="tel"
            id="mobile_number_text_field_display"
            placeholder="Your mobile number"
            value={mobileNumber || ''}
            disabled
         />
         {successMessage && (
            <span className="mobile-number-success">{successMessage}</span>
         )}
         {needsAttention && <MobileNumberRequirement />}
         {needsAttention && (
            <MobileNumberWarning
               messages={getViewWarningMessages(
                  mobileNumber,
                  validationDetails
               )}
            />
         )}
         {/* Rendered after the warning so Change reads as the way to resolve it. */}
         <div className="mobile-number-text-field-actions">
            <DefaultButton
               className="btn"
               size="btn-small"
               type="tertiary"
               text="Change"
               onClick={onEdit}
               disabled={isLoading}
            />
         </div>
      </div>
   );
};

export default MobileNumberView;
